import React, { useEffect, useState } from "react";
import moment from "moment";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import Select from "react-select";
import {
  LuAlertCircle,
  LuArrowUpDown,
  LuCalendar,
  LuFileText,
  LuIndianRupee,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Button from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogInputClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldLabelClass,
  formFieldIconClass,
  formSelectIconClass,
} from "../ui/styles";
import {
  getEditPayment,
  setEditPayment,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import { iconSelectStyles } from "../../static/styles";
import { IFormInputNote, OptionsProps } from "../../types/payments";

type Props = {
  handleSavePayment: (data: IFormInputNote) => void;
};

const NoteForm = ({ handleSavePayment }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const editPayment = useAppSelector(getEditPayment);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [noteTypeOption] = useState<OptionsProps[]>([
    { value: 0, label: "વધારાનું" },
    { value: 1, label: "રાઉન્ડ ઑફ" },
    { value: 2, label: "ટેક્સ અમાઉન્ટ" },
    { value: 3, label: "ક્યુઓલિટી ના લીધે રદ કરેલ" },
    { value: 4, label: "ઓછા નંગ ના લીધે રદ કરેલ" },
    { value: 5, label: "LB" },
    { value: 6, label: "Custom Note" },
  ]);
  const [noteStatusOption] = useState<OptionsProps[]>([
    { value: 0, label: "અમાઉન્ટ ઉમેરો" },
    { value: 1, label: "અમાઉન્ટ બાદ કરો" },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IFormInputNote>({
    defaultValues: {
      amount: null,
      note: null,
      status: null,
      customNote: null,
      date: moment().format("YYYY-MM-DDTHH:mm"),
    },
  });

  const noteData = watch("note");

  const handleOnSubmit: SubmitHandler<IFormInputNote> = async (data: any) => {
    setIsLoading(true);
    const note =
      noteTypeOption?.find((n) => n.value === data.note && n.value !== 6)
        ?.label || data.customNote;

    const payload = {
      ...data,
      date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
    };

    if (editPayment && editPayment.id) {
      await handleSavePayment({
        ...payload,
        id: editPayment.id,
        type: 2,
        note: note,
      });
    } else {
      await handleSavePayment({
        ...payload,
        type: 2,
        party_id: id,
        note: note,
      });
    }
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
    dispatch(setEditPayment(null));
  };

  useEffect(() => {
    if (editPayment && editPayment.id) {
      const note = 6;

      reset({
        amount: editPayment.amount,
        note: note,
        customNote: note === 6 ? editPayment.note : null,
        status: editPayment.type,
        date: moment(editPayment.date || editPayment.created_at).format(
          "YYYY-MM-DDTHH:mm"
        ),
      });
    } else {
      reset({
        amount: null,
        note: 6,
        status: null,
        customNote: null,
        date: moment().format("YYYY-MM-DDTHH:mm"),
      });
    }
  }, [editPayment]);

  return (
    <Dialog open onClose={handleCloseFormModal}>
      <DialogContent
        size="md"
        className={cx("max-h-[90vh] overflow-y-auto", dialogMobileContentClass)}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <DialogTitle className={dialogTitleClass}>
            વધ ઘટ ની માહિતી
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuCalendar className={formSelectIconClass} />
                <Input
                  type="datetime-local"
                  className={dialogInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>

              {errors.date && (
                <p className={fieldErrorTextClass}>તારીખ અને સમય દાખલ કરો</p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                વધ ઘટ નો પ્રકાર <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuArrowUpDown className={formSelectIconClass} />
                <Controller
                  {...register("status", { required: true })}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        noteStatusOption.find(
                          (val) => val.value === field.value
                        ) || null
                      }
                      options={noteStatusOption}
                      placeholder="વધ ઘટ નો પ્રકાર સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.status && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(val: any) => setValue("status", val?.value)}
                    />
                  )}
                />
              </div>

              {errors.status && (
                <p className={fieldErrorTextClass}>
                  વધ ઘટ નો પ્રકાર સિલેક્ટ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                વધ ઘટ નું અમાઉન્ટ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuIndianRupee className={formFieldIconClass} />
                <Input
                  type="number"
                  step="any"
                  placeholder="વધ ઘટ નું અમાઉન્ટ દાખલ કરો"
                  className={dialogInputClass(!!errors.amount)}
                  {...register("amount", { required: true, min: 0 })}
                />
                {errors.amount && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>

              {errors.amount && (
                <p className={fieldErrorTextClass}>
                  વધ ઘટ નું અમાઉન્ટ દાખલ કરો
                </p>
              )}
            </div>

            <div className={cx("space-y-2", noteData !== 6 && "hidden")}>
              <label className={fieldLabelClass}>
                વધ ઘટ ની નોંધ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuFileText className={formFieldIconClass} />
                <Input
                  placeholder="વધ ઘટ ની નોંધ દાખલ કરો"
                  className={dialogInputClass(!!errors.customNote)}
                  {...register("customNote", { required: noteData === 6 })}
                />
                {errors.customNote && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>

              {errors.customNote && (
                <p className={fieldErrorTextClass}>વધ ઘટ ની નોંધ દાખલ કરો</p>
              )}
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              બંધ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading && <span className={buttonSpinnerClass} />}
              આગળ વધો
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NoteForm;
